import { DecimalPipe, NgClass, AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatList } from '@angular/material/list';
import { MatSlideToggleChange, MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTableDataSource } from '@angular/material/table';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { ButtonItem, PageToolbarComponent } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SiteActions, SiteSelectors } from '@next/core-lib/site';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { CowDetailsDialogComponent } from 'src/app/shared/components/cow-details-dialog/cow-details-dialog.component';
import { SiteState } from 'src/app/shared/models';

import { Production } from 'src/app/shared/models/production.model';

import { Attention, CowListItem } from './../../models/production.model';
import * as AttentionLimitsActions from '../../store/actions/attention-limits.actions';
import * as ProductionActions from '../../store/actions/production.actions';
import { getAttentionLimitsByDate } from './../../store/selectors/attention-limits.selectors';
import { getCowList } from './../../store/selectors/production.selectors';
import { ProductionPickerComponent } from '../../../shared/components/production-picker/production-picker.component';

enum ButtonAction {
  settings = 'settings',
}
@Component({
  selector: 'app-individual-production',
  templateUrl: './individual-production.component.html',
  styleUrls: ['./individual-production.component.scss'],
  imports: [
    PageToolbarComponent,
    ProductionPickerComponent,
    MatSlideToggle,
    FormsModule,
    MatList,
    TableComponent,
    NgClass,
    MatButton,
    AsyncPipe,
    DecimalPipe,
    TranslatePipe,
  ],
})
export class IndividualProductionComponent implements AfterViewInit, OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly dialogService = inject(DialogService);
  private readonly locale = inject(LOCALE_ID);
  private readonly localeService = inject(LocaleService);

  @ViewChild('valueAttentionColumnTemplate') valueAttentionColumnTemplate: TemplateRef<unknown>;
  @ViewChild('cowDetailsTemplate') cowDetailsTemplate: TemplateRef<CowListItem>;

  public showOnlyAttentions = true;
  public tableConfig: TableConfig;
  public buttons: ButtonItem[] = [];
  public pageTitle$ = this.store.select(SiteSelectors.selectPageTitle);
  private getCowList$ = this.store.select(getCowList);
  private getAttentionLimitsByDate$ = this.store.select(getAttentionLimitsByDate);
  private readonly destroy$ = new Subject<void>();
  private hasAtLeastAdministratorRoleForApplicationKey$ = this.store.select(
    AuthorizationSelectors.hasAtLeastAdministratorRoleForApplicationKey('DairyResults'),
  );
  private cowList: CowListItem[] = [];

  constructor() {
    this.tableConfig = {
      dataSource: new MatTableDataSource<CowListItem>(),
      pagination: false,
      selectColumn: false,
      alternateRows: true,
      columns: [
        {
          id: 'number',
          label: 'dairyresults.individual-production.table.number',
          type: TableColumnTypes.NUMBER,
        },
        {
          id: 'name',
          label: 'dairyresults.individual-production.table.name',
          type: TableColumnTypes.TEMPLATE,
          template: this.cowDetailsTemplate,
        },
        {
          id: 'parity',
          label: 'dairyresults.individual-production.table.parity',
          type: TableColumnTypes.NUMBER,
        },
        {
          id: 'lactationDays',
          label: 'dairyresults.individual-production.table.lactation-days',
          type: TableColumnTypes.NUMBER,
        },
        {
          id: 'kgMilkExpected',
          label: 'dairyresults.individual-production.table.kg-milk-predicted',
          type: TableColumnTypes.NUMBER,
          digitsInfo: '1.1-1',
        },
        {
          id: 'kgMilk',
          label: 'dairyresults.individual-production.table.kg-milk',
          type: TableColumnTypes.TEMPLATE,
          className: 'mat-cell--template--numeric',
          headerClassName: 'mat-header-cell--template--numeric',
          digitsInfo: '1.1-1',
        },
        {
          id: 'fatPercentage',
          label: 'dairyresults.individual-production.table.fat-percentage',
          type: TableColumnTypes.TEMPLATE,
          className: 'mat-cell--template--numeric',
          headerClassName: 'mat-header-cell--template--numeric',
          digitsInfo: '1.2-2',
        },
        {
          id: 'proteinPercentage',
          label: 'dairyresults.individual-production.table.protein-percentage',
          type: TableColumnTypes.TEMPLATE,
          className: 'mat-cell--template--numeric',
          headerClassName: 'mat-header-cell--template--numeric',
          digitsInfo: '1.2-2',
        },
        {
          id: 'isk',
          label: 'dairyresults.individual-production.table.isk',
          type: TableColumnTypes.NUMBER,
          digitsInfo: '1.1-1',
        },
        {
          id: 'lactation',
          label: 'dairyresults.individual-production.table.lactation',
          type: TableColumnTypes.NUMBER,
        },
        {
          id: 'fatPercentageLowerThenProteinPercentage',
          label: 'dairyresults.individual-production.table.fatPercentageLowerThenProteinPercentage',
          type: TableColumnTypes.STRING,
        },
        {
          id: 'maximumFatProteinPercentage',
          label: 'dairyresults.individual-production.table.maximumFatProteinPercentage',
          type: TableColumnTypes.STRING,
        },
      ],
    };
  }

  ngOnInit(): void {
    this.hasAtLeastAdministratorRoleForApplicationKey$
      .pipe(take(1))
      .subscribe((hasAtLeastAdministratorRoleForApplicationKey) => {
        if (hasAtLeastAdministratorRoleForApplicationKey) {
          this.buttons = [
            {
              icon: 'settings',
              key: ButtonAction.settings,
              viewValue: this.localeService.translate(
                'dairyresults.individual-production.settings.button-text',
              ),
            },
          ];
        }
      });

    this.store.dispatch(
      SiteActions.updatePageTitle({ pageTitle: 'dairyresults.individual-production.title' }),
    );
    this.getCowList$
      .pipe(
        filter((cowList) => !!cowList),
        takeUntil(this.destroy$),
      )
      .subscribe((cowList) => {
        this.cowList = cowList.map((item) => ({
          ...item,
          fatPercentageLowerThenProteinPercentage: item.attentions.includes(
            Attention.fatPercentageLowerThenProteinPercentage,
          )
            ? 'X'
            : '',
          maximumFatProteinPercentage: item.attentions.includes(
            Attention.maximumFatProteinPercentage,
          )
            ? 'X'
            : '',
        }));
        this.setDataSource();
      });
    this.getAttentionLimitsByDate$
      .pipe(
        filter((result) => !!result),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        const maximumFatProteinPercentage = new DecimalPipe(this.locale).transform(
          result.attentionLimits.production.maximumFatProteinPercentage,
          '1.2-2',
        );

        this.tableConfig.columns[11].label = this.localeService.translate(
          `dairyresults.individual-production.table.maximumFatProteinPercentage`,
          { value: maximumFatProteinPercentage },
        );
      });
  }
  ngAfterViewInit() {
    setTimeout(() => {
      const newConfig = { ...this.tableConfig };
      newConfig.columns.forEach((column) => {
        if (['kgMilk', 'fatPercentage', 'proteinPercentage'].includes(column.id)) {
          column.template = this.valueAttentionColumnTemplate;
        }
        if (column.id === 'name') {
          column.template = this.cowDetailsTemplate;
        }
      });
      this.tableConfig = newConfig;
    }, 50);
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onProductionChanged(production: Production): void {
    this.store.dispatch(
      ProductionActions.loadCowListRequest({ date: new Date(production.date).toISOString() }),
    );
    this.store.dispatch(
      AttentionLimitsActions.loadAttentionLimitsRequest({
        date: new Date(production.date).toISOString(),
      }),
    );
  }

  public onButtonAction(data: string) {
    if (data === ButtonAction.settings) {
      this.store.dispatch(AttentionLimitsActions.editAttentionLimits());
    }
  }

  public setAttention(columnId: string, value: CowListItem): string {
    if (columnId === 'kgMilk' && value.attentions.includes(Attention.decreasePercentageKgMilk)) {
      return 'attention';
    }
    if (columnId === 'fatPercentage' && value.attentions.includes(Attention.minimumFatpercentage)) {
      return 'attention';
    }
    if (
      columnId === 'proteinPercentage' &&
      value.attentions.includes(Attention.minimumProteinPercentage)
    ) {
      return 'attention';
    }
  }

  public openCowDetails({ internationalCowId }: CowListItem): void {
    this.dialogService
      .fullscreenDialog(CowDetailsDialogComponent, {
        internationalCowId,
        title: this.localeService.translate('dairyresults.cow-details.title'),
        setPendingChanges: false,
        fullscreen: true,
      })
      .subscribe(({ status }) => {
        if (status === DIALOG_RETURN_TYPES.confirm) {
          // Handle the confirmation status but do nothing
        }
      });
  }

  public showOnlyAttentionsChange(event: MatSlideToggleChange) {
    this.showOnlyAttentions = event.checked;
    this.setDataSource();
  }

  private setDataSource() {
    if (this.showOnlyAttentions) {
      this.tableConfig.dataSource.data = this.cowList.filter(
        (cow) =>
          cow.attentions.includes(Attention.decreasePercentageKgMilk) ||
          cow.attentions.includes(Attention.minimumFatpercentage) ||
          cow.attentions.includes(Attention.minimumProteinPercentage) ||
          cow.attentions.includes(Attention.maximumFatProteinPercentage) ||
          cow.attentions.includes(Attention.fatPercentageLowerThenProteinPercentage),
      );
    } else {
      this.tableConfig.dataSource.data = this.cowList;
    }
  }
}
